import { actionChannel, delay, fork, put, select, take } from 'redux-saga/effects';

import type { SagaIterator } from '@redux-saga/types';

import { DEFAULT_TRANSITION_DURATION } from 'app/constants/animation';

import { ADD_BIG_STAKE, AddBigStake } from '../../actions/bigStakes';
import { deleteFromBetSlip, RemoveEntryProps, setupEntryAmount } from '../../actions/entries';
import {
  failureSubmissionEnd,
  SUBMIT_STAKE_FAILURE, SUBMIT_STAKE_SUCCESS, SubmitStakeFailure,
  SubmitStakeSuccess,
} from '../../actions/submission';
import { BetErrorCode, DISPLAY_RESULTS_TIMEOUT } from '../../constants';
import { selectBigStakes } from '../../selectors/bigStakes';
import {
  selectBets,
  selectMatches,
  selectSports,
  selectStakes,
  selectTotalAmountActive,
} from '../../selectors/entries';
import { BigStakes } from '../../types/bigStakes';
import { EntriesBets, EntriesMatches, EntriesSports, EntriesStakes } from '../../types/entries';
import { BetsInfo } from '../../types/stakeTypes/singles';

// ---

type WatchProcessActions =
  | SubmitStakeSuccess
  | SubmitStakeFailure
  | AddBigStake;

type BetId = number;
type AmountValue = number;

function* watchStakeProcessComplete(entriesSize: number, betsInfo: BetsInfo): SagaIterator {
  let entriesWithDecision = 0;
  const failureStakes = new Map<BetId, AmountValue>();

  const channel = yield actionChannel([SUBMIT_STAKE_SUCCESS, SUBMIT_STAKE_FAILURE, ADD_BIG_STAKE]);

  const totalAmountActive: boolean = yield select(selectTotalAmountActive);

  while (true) {
    const action: WatchProcessActions = yield take(channel);

    const bigStakes: BigStakes = yield select(selectBigStakes);

    const isBigStakeSubmitAction =
      action.type !== ADD_BIG_STAKE
      && bigStakes.has(action.payload.dialogId);

    if (isBigStakeSubmitAction || !action.payload.requestPayload) {
      continue;
    }

    const payloadBets = action.payload.requestPayload.bets;

    switch (action.type) {
      //Закоменнтировано в сязи с переходом на новое флоу при совершениие крупной ставки
      // case ADD_BIG_STAKE: {
      //   yield fork(deleteEntries, action);
      //   break;
      // }

      case SUBMIT_STAKE_FAILURE: {
        const isOnlyOneBet = payloadBets.length === 1;

        if (isOnlyOneBet && totalAmountActive) {
          failureStakes.set(payloadBets[0].id, action.payload.requestPayload.amount);
        }

        let hasPriceChangeError = false;
        const matchIds = new Set<number>();

        action.payload?.betErrors?.forEach((betError) => {
          if (betError.code === BetErrorCode.PRICE_CHANGED) {
            matchIds.has(betError.matchId)
              ? hasPriceChangeError = true
              : matchIds.add(betError.matchId);
          }
        });
        const betIds = action.payload.requestPayload.bets.map((bet) => bet.id);
        yield fork(failureSubmission, payloadBets.length, hasPriceChangeError, betIds);
        break;
      }

      case SUBMIT_STAKE_SUCCESS: {
        yield fork(deleteEntries, action);
        break;
      }
    }

    entriesWithDecision += payloadBets.length;
    const isLastEntry = entriesWithDecision === entriesSize;

    if (isLastEntry) {
      if (failureStakes.size === 1) {
        const [[stakeId, stakeAmount]] = failureStakes;
        const info = betsInfo.get(stakeId);

        const account = info?.amount?.account;

        if (account) {
          yield put(setupEntryAmount(stakeId, stakeAmount.toString(), account));
        }
      }

      return;
    }
  }
}

// ---

function* deleteEntries(action: SubmitStakeSuccess | AddBigStake) {
  if (!action.payload.requestPayload) {
    return;
  }

  const bets: EntriesBets = yield select(selectBets);
  const stakes: EntriesStakes = yield select(selectStakes);
  const matches: EntriesMatches = yield select(selectMatches);
  const sports: EntriesSports = yield select(selectSports);

  if (action.type !== ADD_BIG_STAKE) {
    // let user to see the results
    yield delay(Math.max(DISPLAY_RESULTS_TIMEOUT, DEFAULT_TRANSITION_DURATION + 10));
  }

  const payloadBets = action.payload.requestPayload.bets;

  const entries = payloadBets.reduce((acc: RemoveEntryProps[], { id }) => {
    const bet = bets.get(id);
    const stake = bet && stakes.get(bet.entity.stakeId);
    const match = stake && matches.get(stake.entity.matchId);
    const sport = match && sports.get(match.entity.sportId);

    if (!bet || !stake || !match || !sport) {
      return acc;
    }

    acc.push({
      betId: bet.entity.id,
      stakeId: stake.entity.id,
      matchId: match.entity.id,
      sportId: sport.entity.id,
    });

    return acc;
  }, []);

  yield put(deleteFromBetSlip({ entries }));
}

function* failureSubmission(numberOfBets: number, hasPriceChangeError: boolean, betIds: number[]) {
  // let user to see the results
  yield delay(Math.max(DISPLAY_RESULTS_TIMEOUT, DEFAULT_TRANSITION_DURATION + 10));

  yield put(failureSubmissionEnd(numberOfBets, hasPriceChangeError, betIds));
}

// ---

export default watchStakeProcessComplete;
